import { END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import { PAGINATION_LIMIT } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import { api } from '@core/baseApi';
import { mapCreateFormResponse } from '@features/forms/helpers/mapCreateFormResponse';
import { mapFormActivityLog } from '@features/forms/helpers/mapFormActivityLog';
import { mapFormDropOffs } from '@features/forms/helpers/mapFormDropOff';
import { mapFormListApi } from '@features/forms/helpers/mapFormListApi';
import { mapFormOverviewToDetail } from '@features/forms/helpers/mapFormOverviewToDetail';
import { mapFormShareSourcesApi } from '@features/forms/helpers/mapFormShareLinksApi';
import { mapUpdateFormSourcesResponse } from '@features/forms/helpers/mapFormSourceApi';
import { mapFormStatusActionToApiStatus } from '@features/forms/helpers/mapFormStatusActionToApiStatus';
import { mapFormStatusCounts } from '@features/forms/helpers/mapFormStatusCounts';
import { mapFormSubmissions } from '@features/forms/helpers/mapFormSubmission';
import { mapSubmissionDetailToDrawer } from '@features/forms/helpers/mapFormSubmissionDetail';
import { mapFormTemplatesApi } from '@features/forms/helpers/mapFormTemplatesApi';
import { mapUpdateFormResponse } from '@features/forms/helpers/mapUpdateFormResponse';
import { mapUpdateFormStatusResponse } from '@features/forms/helpers/mapUpdateFormStatusResponse';
import { mapWizardToCreateFormRequest } from '@features/forms/helpers/mapWizardToCreateFormRequest';
import { mapWizardToUpdateFormRequest } from '@features/forms/helpers/mapWizardToUpdateFormRequest';
import { FormActivityLogApiResponse } from '@features/forms/types/formActivityLogApi';
import {
  FormDropOffsApiResponse,
  FormDropOffsExportApiResponse
} from '@features/forms/types/formDropOffApi';
import { FormOverviewApiResponse } from '@features/forms/types/formOverviewApi';
import {
  FormSubmissionsApiResponse,
  FormSubmissionsExportApiResponse
} from '@features/forms/types/formSubmissionApi';
import { FormSubmissionDetailApiResponse } from '@features/forms/types/formSubmissionDetailApi';
import { SubmissionDrawerData } from '@features/forms/types/submissionDrawer';
import { HTTPResponse } from '@types';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';

import {
  toActivityLogQueryParams,
  toDropOffsQueryParams,
  toFormsListQueryParams,
  toSubmissionsQueryParams
} from '../helpers/queryParams';
import {
  CreateFormPayload,
  CreateFormResponse,
  DuplicateFormPayload,
  ExportFormDropOffsParams,
  ExportFormSubmissionsParams,
  FormActivityLogListResponse,
  FormDetail,
  FormDropOffsListResponse,
  FormShareSourcesResponse,
  FormsListResponse,
  FormStatusCounts,
  FormSubmissionsListResponse,
  FormTemplate,
  GetActivityLogParams,
  GetDropOffsParams,
  GetFormByIdParams,
  GetFormShareLinksParams,
  GetFormsQueryParams,
  GetFormStatusCountsParams,
  GetFormTemplatesParams,
  GetSubmissionDetailParams,
  GetSubmissionsParams,
  UpdateFormPayload,
  UpdateFormSourcesPayload,
  UpdateFormSourcesResponse,
  UpdateFormStatusPayload,
  UpdateFormStatusResponse
} from '../types';
import { FormCreateResponseApi } from '../types/formCreateApi';
import { FormListApiResponse } from '../types/formListApi';
import { FormShareLinksApiResponse } from '../types/formShareLinksApi';
import { UpdateFormSourcesApiResponse } from '../types/formSourceApi';
import { FormTemplatesApiResponse } from '../types/formTemplatesApi';

const getOrgBranchIds = (): {
  organizationId: string;
  branchId: string;
} | null => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);
  if (!organizationId || !branchId) {
    return null;
  }
  return { organizationId, branchId };
};

const extractJobId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const record = payload as Record<string, unknown>;
  if (typeof record.job_id === 'string') {
    return record.job_id;
  }
  if (record.data && typeof record.data === 'object') {
    const nested = record.data as Record<string, unknown>;
    if (typeof nested.job_id === 'string') {
      return nested.job_id;
    }
  }
  return undefined;
};

const formsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getForms: builder.query<FormsListResponse, GetFormsQueryParams>({
      async queryFn(
        { organizationId, branchId, ...params },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const queryParams = {
          page: 0,
          limit: PAGINATION_LIMIT,
          ...params
        };
        const queryString = buildQueryString(
          toFormsListQueryParams(queryParams)
        );

        const result = await fetchWithBQ({
          url: `${END_POINTS.FORMS.LIST(
            organizationId,
            branchId
          )}${queryString}`,
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as FormListApiResponse;
        return {
          data: {
            data: mapFormListApi(response.data),
            pagination: {
              page: response.pagination.page,
              limit: response.pagination.limit,
              totalRows: response.pagination.total_rows,
              lastPage: response.pagination.last_page
            }
          }
        };
      },
      providesTags: [ApiTags.FORM_LIST]
    }),
    getFormStatusCounts: builder.query<
      FormStatusCounts,
      GetFormStatusCountsParams
    >({
      async queryFn(
        { organizationId, branchId },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.STATUS_COUNTS(organizationId, branchId),
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: mapFormStatusCounts(result.data) };
      },
      providesTags: [ApiTags.FORM_LIST]
    }),
    getFormTemplates: builder.query<FormTemplate[], GetFormTemplatesParams>({
      async queryFn({ organizationId }, _api, _extraOptions, fetchWithBQ) {
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.TEMPLATES(organizationId),
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as FormTemplatesApiResponse;
        return { data: mapFormTemplatesApi(response.data ?? []) };
      },
      providesTags: [ApiTags.FORM_TEMPLATES]
    }),
    getFormById: builder.query<FormDetail, GetFormByIdParams>({
      async queryFn(
        { organizationId, branchId, formId },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.OVERVIEW(organizationId, branchId, formId),
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapFormOverviewToDetail(result.data as FormOverviewApiResponse)
        };
      },
      providesTags: (result, error, { formId }) => [
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    createForm: builder.mutation<CreateFormResponse, CreateFormPayload>({
      async queryFn({ wizardData }, _api, _extraOptions, fetchWithBQ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const body = await mapWizardToCreateFormRequest(wizardData);
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.CREATE(ids.organizationId, ids.branchId),
          method: 'POST',
          body
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapCreateFormResponse(result.data as FormCreateResponseApi)
        };
      },
      invalidatesTags: [ApiTags.FORM_LIST]
    }),
    updateForm: builder.mutation<
      ReturnType<typeof mapUpdateFormResponse>,
      UpdateFormPayload
    >({
      async queryFn(
        { formId, wizardData, originalFields },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const body = await mapWizardToUpdateFormRequest(
          wizardData,
          originalFields
        );
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.UPDATE(
            ids.organizationId,
            ids.branchId,
            formId
          ),
          method: 'PUT',
          body
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapUpdateFormResponse(result.data as FormCreateResponseApi)
        };
      },
      invalidatesTags: (result, error, { formId }) => [
        ApiTags.FORM_LIST,
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    duplicateForm: builder.mutation<{ formId: string }, DuplicateFormPayload>({
      query: ({ formId }) => ({
        url: END_POINTS.FORMS.DUPLICATE(formId),
        method: 'POST'
      }),
      invalidatesTags: [ApiTags.FORM_LIST]
    }),
    updateFormStatus: builder.mutation<
      UpdateFormStatusResponse,
      UpdateFormStatusPayload
    >({
      async queryFn({ formId, action }, _api, _extraOptions, fetchWithBQ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const status = mapFormStatusActionToApiStatus(action);
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.UPDATE_STATUS(
            ids.organizationId,
            ids.branchId,
            formId,
            status
          ),
          method: 'PATCH'
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapUpdateFormStatusResponse(
            result.data as FormCreateResponseApi
          )
        };
      },
      invalidatesTags: (result, error, { formId }) => [
        ApiTags.FORM_LIST,
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    getFormSubmissions: builder.query<
      FormSubmissionsListResponse,
      GetSubmissionsParams
    >({
      async queryFn(params, _api, _extraOptions, fetchWithBQ) {
        const {
          organizationId,
          branchId,
          formId,
          page = 0,
          limit = PAGINATION_LIMIT,
          search
        } = params;

        const queryString = buildQueryString(
          toSubmissionsQueryParams({ page, limit, search })
        );
        const result = await fetchWithBQ({
          url: `${END_POINTS.FORMS.SUBMISSIONS(
            organizationId,
            branchId,
            formId
          )}${queryString}`,
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as FormSubmissionsApiResponse;
        return {
          data: {
            data: mapFormSubmissions(response.data),
            pagination: {
              page: response.pagination.page,
              limit: response.pagination.limit,
              totalRows: response.pagination.total_rows,
              lastPage: response.pagination.last_page
            }
          }
        };
      },
      providesTags: (result, error, { formId }) => [
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    getFormSubmissionDetail: builder.query<
      SubmissionDrawerData,
      GetSubmissionDetailParams
    >({
      async queryFn(
        { organizationId, branchId, formId, submissionId },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.SUBMISSION_BY_ID(
            organizationId,
            branchId,
            formId,
            submissionId
          ),
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapSubmissionDetailToDrawer(
            result.data as FormSubmissionDetailApiResponse
          )
        };
      },
      providesTags: (result, error, { formId, submissionId }) => [
        { type: ApiTags.FORM_DETAIL, id: `${formId}-${submissionId}` }
      ]
    }),
    exportFormSubmissions: builder.mutation<
      { jobId: string },
      ExportFormSubmissionsParams
    >({
      async queryFn(
        { formId, includeFormData },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const queryString = buildQueryString({
          include_form_data: includeFormData
        });
        const result = await fetchWithBQ({
          url: `${END_POINTS.FORMS.SUBMISSIONS_EXPORT(
            ids.organizationId,
            ids.branchId,
            formId
          )}${queryString}`,
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const jobId = extractJobId(
          result.data as FormSubmissionsExportApiResponse
        );
        if (!jobId) {
          return {
            error: {
              status: 500,
              data: 'Export job id missing from response'
            }
          };
        }

        return { data: { jobId } };
      }
    }),
    getFormDropOffs: builder.query<FormDropOffsListResponse, GetDropOffsParams>(
      {
        async queryFn(params, _api, _extraOptions, fetchWithBQ) {
          const {
            organizationId,
            branchId,
            formId,
            page = 0,
            limit = PAGINATION_LIMIT,
            search,
            status
          } = params;

          const queryString = buildQueryString(
            toDropOffsQueryParams({ page, limit, search, status })
          );
          const result = await fetchWithBQ({
            url: `${END_POINTS.FORMS.DROP_OFFS(
              organizationId,
              branchId,
              formId
            )}${queryString}`,
            method: 'GET'
          });

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data as FormDropOffsApiResponse;
          return {
            data: {
              data: mapFormDropOffs(response.data),
              pagination: {
                page: response.pagination.page,
                limit: response.pagination.limit,
                totalRows: response.pagination.total_rows,
                lastPage: response.pagination.last_page
              }
            }
          };
        },
        providesTags: (result, error, { formId }) => [
          { type: ApiTags.FORM_DETAIL, id: formId }
        ]
      }
    ),
    exportFormDropOffs: builder.mutation<
      { jobId: string },
      ExportFormDropOffsParams
    >({
      async queryFn(
        { formId, includeFormData },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const queryString = buildQueryString({
          include_form_data: includeFormData
        });
        const result = await fetchWithBQ({
          url: `${END_POINTS.FORMS.DROP_OFFS_EXPORT(
            ids.organizationId,
            ids.branchId,
            formId
          )}${queryString}`,
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const jobId = extractJobId(
          result.data as FormDropOffsExportApiResponse
        );
        if (!jobId) {
          return {
            error: {
              status: 500,
              data: 'Export job id missing from response'
            }
          };
        }

        return { data: { jobId } };
      }
    }),
    getFormActivityLog: builder.query<
      FormActivityLogListResponse,
      GetActivityLogParams
    >({
      async queryFn(
        {
          organizationId,
          branchId,
          formId,
          page = 0,
          limit = PAGINATION_LIMIT
        },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const queryString = buildQueryString(
          toActivityLogQueryParams({ page, limit })
        );
        const result = await fetchWithBQ({
          url: `${END_POINTS.FORMS.ACTIVITY_LOG(
            organizationId,
            branchId,
            formId
          )}${queryString}`,
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as FormActivityLogApiResponse;
        return {
          data: {
            data: mapFormActivityLog(response),
            pagination: {
              page: response.pagination.page,
              limit: response.pagination.limit,
              totalRows: response.pagination.total_rows,
              lastPage: response.pagination.last_page
            }
          }
        };
      },
      providesTags: (result, error, { formId }) => [
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    getFormShareLinks: builder.query<
      FormShareSourcesResponse,
      GetFormShareLinksParams
    >({
      async queryFn(
        { organizationId, branchId, formId },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.SHARE_LINKS(organizationId, branchId, formId),
          method: 'GET'
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as FormShareLinksApiResponse;
        return { data: mapFormShareSourcesApi(response) };
      },
      providesTags: (result, error, { formId }) => [
        { type: ApiTags.FORM_DETAIL, id: formId }
      ]
    }),
    updateFormSources: builder.mutation<
      UpdateFormSourcesResponse,
      UpdateFormSourcesPayload
    >({
      async queryFn(
        { formId, enable, sources = [] },
        _api,
        _extraOptions,
        fetchWithBQ
      ) {
        const ids = getOrgBranchIds();
        if (!ids) {
          return {
            error: {
              status: 400,
              data: 'Organization or branch is not selected'
            }
          };
        }

        const result = await fetchWithBQ({
          url: END_POINTS.FORMS.SOURCE(
            ids.organizationId,
            ids.branchId,
            formId
          ),
          method: 'POST',
          body: { enable, sources }
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: mapUpdateFormSourcesResponse(
            result.data as UpdateFormSourcesApiResponse
          )
        };
      },
      async onQueryStarted({ formId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const ids = getOrgBranchIds();
          if (!ids) {
            return;
          }
          const formByIdArgs: GetFormByIdParams = {
            organizationId: ids.organizationId,
            branchId: ids.branchId,
            formId
          };
          const shareLinksArgs: GetFormShareLinksParams = formByIdArgs;
          dispatch(
            formsApi.util.updateQueryData(
              'getFormById',
              formByIdArgs,
              (draft) => {
                draft.sourceTrackingEnabled = data.enable;
                if (!data.enable) {
                  draft.sourceBreakdown = undefined;
                }
              }
            )
          );
          dispatch(
            formsApi.util.updateQueryData(
              'getFormShareLinks',
              shareLinksArgs,
              () => ({
                enable: data.enable,
                sources: data.sources
              })
            )
          );
        } catch {
          // Local toggle state is reverted in useFormShareSourceActions
        }
      },
      invalidatesTags: [ApiTags.FORM_LIST, ApiTags.FORM_DETAIL]
    }),
    getFormPresignedUrl: builder.query<
      {
        file_id: string;
        access_key: string;
        presigned_url: string;
      },
      { branchId: string; fileName: string }
    >({
      query: ({ branchId, fileName }) => ({
        url: END_POINTS.FORMS.GET_PRESIGNED_URL(branchId, fileName),
        method: 'GET'
      })
    }),
    downloadFormFile: builder.query<
      HTTPResponse<unknown>,
      { fileId: string; accessKey: string; signature: string }
    >({
      query: ({ fileId, accessKey, signature }) => ({
        url: END_POINTS.FORMS.DOWNLOAD_FILE(fileId, accessKey, signature),
        method: 'GET'
      })
    })
  })
});

export const {
  useGetFormsQuery,
  useGetFormStatusCountsQuery,
  useGetFormTemplatesQuery,
  useGetFormByIdQuery,
  useLazyGetFormByIdQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDuplicateFormMutation,
  useUpdateFormStatusMutation,
  useGetFormSubmissionsQuery,
  useGetFormSubmissionDetailQuery,
  useExportFormSubmissionsMutation,
  useGetFormDropOffsQuery,
  useExportFormDropOffsMutation,
  useGetFormActivityLogQuery,
  useLazyGetFormActivityLogQuery,
  useGetFormShareLinksQuery,
  useUpdateFormSourcesMutation,
  useLazyGetFormPresignedUrlQuery,
  useLazyDownloadFormFileQuery
} = formsApi;

export default formsApi;
